import { NgControl } from '@angular/forms';
import {
  ChangeDetectorRef, Directive, HostListener, Input, OnDestroy, OnInit, Optional, Self,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AeroInputComponent } from '../../aero/aero-input/aero-input.component';
import { TranslationService } from '../../common/translation.service';

/** Displays translated validation errors for controls marked with `bulbValidationField`. */
@Directive({
  selector: '[bulbValidationField]',
  standalone: false,
})
export class BulbValidationFieldDirective implements OnInit, OnDestroy {
  private statusSub: Subscription;

  @Input() errorCodeOverride: string;

  /** Creates the directive with the control, input component, and translation dependencies. */
  constructor(
    @Self() @Optional() private ngControl: NgControl,
    private translate: TranslationService,
    @Optional() private aeroInput: AeroInputComponent,
    private cdr: ChangeDetectorRef,
  ) {}

  /** Subscribes to control status changes so displayed errors stay current. */
  ngOnInit(): void {
    if (this.ngControl) {
      this.statusSub = this.ngControl.statusChanges.subscribe(() => {
        this.updateErrorMessage();
      });
    }
  }

  @HostListener('focusout')
  /** Updates the displayed validation message when the control loses focus. */
  onBlur(): void {
    this.updateErrorMessage();
  }

  /** Resolves the current validation error to translated text and displays it. */
  private updateErrorMessage(): Promise<void> {
    const control = this.ngControl?.control;
    if (!control) return;

    if (control.valid || control.pending || !control.errors
      || (control.pristine && !control.touched && !control.value)) {
      this.setError('');
      return;
    }

    let code = 'validation_error_standard';
    if (this.errorCodeOverride) {
      code = this.errorCodeOverride;
    }

    let args = {};

    if (control.hasError('required')) {
      code = 'validation_error_required';
    } else if (control.hasError('pattern')) {
      code = 'validation_error_pattern';
    } else if (control.hasError('email')) {
      code = 'validation_error_email';
    } else if (control.hasError('minlength')) {
      code = 'validation_error_min_length';
      args = { value: control.getError('minlength').requiredLength };
    } else if (control.hasError('maxlength')) {
      code = 'validation_error_max_length';
      args = { value: control.getError('maxlength').requiredLength };
    } else if (control.hasError('fieldMatch')) {
      code = 'validation_error_password_match';
    } else if (control.hasError('accountSettingsUsernameUnique')) {
      code = control.errors.code ?? 'validation_username_unique_error';
    } else if (control.hasError('emailUnique')) {
      code = control.errors.code ?? 'validation_email_unique_error';
    } else if (control.hasError('promoCode')) {
      code = control.errors.code ?? 'validation_error_promo_code_invalid';
    }

    this.translate.translateCustomKeys(code, args).then((translation: string) => {
      this.setError(translation);
    });
  }

  /** Updates the Aero input error state and triggers change detection.
   * @param message Translated validation message, or an empty string to clear it.
   */
  private setError(message: string): void {
    if (this.aeroInput) {
      this.aeroInput.errorMessage = message;

      const innerControl = this.aeroInput.aeroInputNgModel?.control;

      if (innerControl) {
        if (message) {
          innerControl.setErrors({ customValidationFailed: true });
          innerControl.markAsTouched();
          innerControl.markAsDirty();
        } else {
          innerControl.updateValueAndValidity({ emitEvent: false });
        }
      }

      this.cdr.detectChanges();
    }
  }

  /** Unsubscribes from control status changes when the directive is destroyed. */
  ngOnDestroy(): void {
    if (this.statusSub) {
      this.statusSub.unsubscribe();
    }
  }
}
